<script setup lang="ts">
// Mounted once in App.vue. Renders the dialog stack, handles Escape
// dismissal, engages body-scroll lock while any dialog is visible, and keeps
// lower entries mounted-but-hidden so their reactive state survives when a
// child dialog is on top.
//
// Backdrop click is deliberately a no-op. Autofill dropdowns, browser
// context menus, and imprecise trackpad clicks all fire outside-clicks that
// have destroyed in-progress form state; every dialog exposes an explicit
// Cancel/close path, and Escape remains the quick keyboard dismissal.

import { computed } from 'vue'

import { closeDialog, useDialogStack } from '../dialogs'
import { onKeydown, useBodyScrollLock } from '../focus'

import DialogEntryHost from './DialogEntryHost.vue'

const { stack } = useDialogStack()

const topIdx   = computed(() => stack.value.length - 1)
const topEntry = computed(() => stack.value[topIdx.value] ?? null)

useBodyScrollLock({ enabled: () => stack.value.length > 0 })

// capture=true so an inner element (currently the engine's canvas) cannot
// swallow Escape via stopPropagation before it reaches us.
onKeydown('Escape', () => {
  const top = topEntry.value
  if (top?.options.dismissible) closeDialog(top.id)
}, { when: () => topEntry.value !== null, capture: true })
</script>

<template>
  <Teleport to="body">
    <div
      v-for="(entry, i) in stack"
      :key="entry.id"
      class="dialog-backdrop"
      :class="{ 'dialog-backdrop--inactive': i !== topIdx }"
      :inert="i !== topIdx || undefined"
      :aria-hidden="i !== topIdx ? 'true' : undefined"
    >
      <DialogEntryHost :entry="entry" :is-top="i === topIdx" />
    </div>
  </Teleport>
</template>
